import { useCallback, useRef } from 'react';

/**
 * Custom hook to throttle scroll events for better performance
 * @param {Function} callback - Function to call on scroll
 * @param {number} delay - Throttle delay in milliseconds (default: 16ms for 60fps)
 * @returns {Function} Throttled scroll handler
 */
const useThrottledScroll = (callback, delay = 16) => {
  const lastCall = useRef(0);
  const lastCallTimer = useRef(null);

  const throttledCallback = useCallback((...args) => {
    const now = Date.now();
    
    if (now - lastCall.current >= delay) {
      lastCall.current = now;
      callback(...args);
    } else {
      // Clear existing timer
      if (lastCallTimer.current) {
        clearTimeout(lastCallTimer.current);
      }
      
      // Set new timer
      lastCallTimer.current = setTimeout(() => {
        lastCall.current = Date.now();
        callback(...args);
      }, delay - (now - lastCall.current));
    }
  }, [callback, delay]);

  return throttledCallback;
};

export default useThrottledScroll; 